import React from "react"
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteClasses,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  Box,
  InputAdornment
} from "@mui/material"
import { FaIcon } from "../../../../mp-core/icons"
import Autocomplete from "../Autocomplete"
import CircleLoader from "../CircleLoader"
import { useStyles } from "./styles"
import { Option } from "../Autocomplete/Autocomplete"
import { StyleName } from "../Autocomplete/styles"

type OuterClasses = Partial<AutocompleteClasses> &
  Partial<Record<StyleName, string>>

type Props = Pick<
  AutocompleteProps<Option, boolean, boolean, boolean>,
  "size" | "PaperComponent" | "componentsProps"
> & {
  value?: string | string[]
  multiple?: boolean
  options: Option[]
  searchInput: string
  placeholder: string
  loading?: boolean
  isNoResults?: boolean
  isNarrowSearch?: boolean
  inputLimit?: number
  outerClasses?: OuterClasses
  onChange?: (
    _event: React.SyntheticEvent,
    option: NonNullable<string | Option> | (string | Option)[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<Option>
  ) => void
  renderOption: (
    optionProps: React.HTMLAttributes<HTMLDivElement>,
    option: Option,
    _state: AutocompleteRenderOptionState
  ) => React.ReactNode
  onSearchInputChange: (value: string) => void
  getChipLabel?: (option: Option) => React.ReactNode
  "data-automation-id"?: string
}

const SearchWithDropdown: React.FC<Props> = ({
  value,
  multiple = false,
  options,
  searchInput,
  placeholder,
  loading = false,
  isNoResults = false,
  isNarrowSearch = false,
  inputLimit,
  outerClasses,
  onChange,
  renderOption,
  onSearchInputChange,
  getChipLabel,
  "data-automation-id": dataAutomationId,
  ...props
}) => {
  const { classes, cx } = useStyles({ hasSearch: !!searchInput })

  const filterOptions = options => options

  const getOptions = () => {
    if (loading) {
      return []
    }

    if (isNarrowSearch) {
      return [{ key: "narrow", value: "narrow", label: "" }]
    }

    if (isNoResults) {
      return [{ key: "no-results", value: "no-results", label: "" }]
    }

    return options
  }

  return (
    <Autocomplete
      inline
      freeSolo
      multiple={!!value && multiple}
      value={value}
      forcePopupIcon={false}
      clearOnBlur={false}
      inputValue={searchInput}
      inputLimit={inputLimit}
      loading={loading}
      loadingText={
        <Box display="flex" justifyContent="center">
          <CircleLoader />
        </Box>
      }
      withAdormentBorder={false}
      placeholder={placeholder}
      inputStartAdornment={
        <InputAdornment position="start" className={classes.inputAdornment}>
          <FaIcon
            solid
            size="xsmall"
            spin={loading}
            name={loading ? "spinner-third" : "search"}
          />
        </InputAdornment>
      }
      options={getOptions()}
      onChange={onChange}
      renderOption={renderOption}
      filterOptions={filterOptions}
      onInputChange={(_e, value) => onSearchInputChange(value || "")}
      getChipLabel={getChipLabel}
      classes={{
        inputRoot: outerClasses?.inputRoot,
        paper: classes.globalSearchAutocompletePaper,
        popper: cx(classes.popper, outerClasses?.popper),
        listbox: outerClasses?.listbox
      }}
      data-automation-id={dataAutomationId}
      onKeyDown={e => e.stopPropagation()}
      {...props}
    />
  )
}

export default SearchWithDropdown
